import React, { useEffect, useRef } from 'react';
import './ContextMenu.css';

const ContextMenu = ({
  isVisible,
  position,
  onClose,
  onCopyText,
  onDeleteWord,
  onUndeleteWord,
  onInsertWord,
  onEditWord,
  onShowAlternatives,
  onPlayWord,
  onPlaySentence,
  onPause,
  hasSelectedText,
  selectedWord,
  hasAlternatives = false,
  isDeleted = false,
  isGhosted = false,
  onChangeSpeaker,
  transcriptSpeakers = [],
  wordSpeaker = null,
  onEditTimestamp,
  onShiftTimestamps,
  onDistributeTimestamps,
  hasMultiWordSelection = false,
  onSplitSpeaker,
  selectedWordRange = null
}) => {
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose();
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [isVisible, onClose]);

  if (!isVisible) return null;

  return (
    <div
      ref={menuRef}
      className="context-menu"
      style={{
        left: position.x,
        top: position.y,
      }}
    >
      <div className="context-menu-content">
        {hasSelectedText && (
          <button
            className="context-menu-item"
            onClick={() => {
              onCopyText();
              onClose();
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
            </svg>
            Copy Selected Text
          </button>
        )}

        {selectedWord && (
          <>
            <button
              className="context-menu-item"
              onClick={() => {
                onEditWord(selectedWord);
                onClose();
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
              </svg>
              Edit Word
            </button>

            {onPlayWord && (
              <button
                className="context-menu-item"
                onClick={() => {
                  onPlayWord(selectedWord);
                  onClose();
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z"/>
                </svg>
                Play Word
              </button>
            )}

            {onPlaySentence && (
              <button
                className="context-menu-item"
                onClick={() => {
                  onPlaySentence(selectedWord);
                  onClose();
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z"/>
                  <circle cx="18" cy="12" r="1" fill="currentColor"/>
                </svg>
                Play Sentence
              </button>
            )}

            {onPause && (
              <button
                className="context-menu-item"
                onClick={() => {
                  onPause();
                  onClose();
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                </svg>
                Pause Audio
              </button>
            )}

            {hasAlternatives && onShowAlternatives && (
              <button
                className="context-menu-item"
                onClick={() => {
                  onShowAlternatives(selectedWord);
                  onClose();
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
                Show Alternatives
              </button>
            )}

            {onChangeSpeaker && transcriptSpeakers.length > 0 && (
              <div className="context-menu-item submenu-container">
                <div className="context-menu-item-content">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                  </svg>
                  Change Speaker
                </div>
                <div className="context-submenu">
                  {transcriptSpeakers.map((speaker) => (
                    <button
                      key={speaker.localLabel}
                      className={`context-menu-item ${wordSpeaker === speaker.localLabel ? 'active' : ''}`}
                      onClick={() => {
                        onChangeSpeaker(selectedWord, speaker.localLabel);
                        onClose();
                      }}
                    >
                      <div style={{
                        display: 'inline-block',
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: speaker.color || '#999',
                        marginRight: '8px'
                      }}></div>
                      {speaker.customLabel || speaker.localLabel}
                      {wordSpeaker === speaker.localLabel && ' ✓'}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {(isDeleted || isGhosted) ? (
              <button
                className="context-menu-item restore"
                onClick={() => {
                  onUndeleteWord(selectedWord);
                  onClose();
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M13 3c-4.97 0-9 4.03-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42C8.27 19.99 10.51 21 13 21c4.97 0 9-4.03 9-9s-4.03-9-9-9zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z"/>
                </svg>
                Undelete Word
              </button>
            ) : (
              <button
                className="context-menu-item delete"
                onClick={() => {
                  onDeleteWord(selectedWord);
                  onClose();
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                </svg>
                Delete Word
              </button>
            )}

            <div className="context-menu-separator"></div>

            <button
              className="context-menu-item"
              onClick={() => {
                onInsertWord(selectedWord, 'before');
                onClose();
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
              </svg>
              Insert Word Before
            </button>

            <button
              className="context-menu-item"
              onClick={() => {
                onInsertWord(selectedWord, 'after');
                onClose();
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
              </svg>
              Insert Word After
            </button>

            <div className="context-menu-separator"></div>

            {onEditTimestamp && (
              <button
                className="context-menu-item"
                onClick={() => {
                  onEditTimestamp(selectedWord);
                  onClose();
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
                </svg>
                Edit Timestamp
              </button>
            )}
          </>
        )}

        {hasMultiWordSelection && (
          <>
            <div className="context-menu-separator"></div>

            {onSplitSpeaker && transcriptSpeakers.length > 0 && selectedWordRange && (
              <div className="context-menu-item submenu-container">
                <div className="context-menu-item-content">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M14 4l2.29 2.29-2.88 2.88 1.42 1.42 2.88-2.88L20 10V4h-6zm-4 0H4v6l2.29-2.29 4.71 4.7V20h2v-8.41l-5.29-5.3L10 4z"/>
                  </svg>
                  Assign Selection to Speaker
                </div>
                <div className="context-submenu">
                  {transcriptSpeakers.map((speaker) => (
                    <button
                      key={speaker.localLabel}
                      className="context-menu-item"
                      onClick={() => {
                        onSplitSpeaker(selectedWordRange, speaker.localLabel);
                        onClose();
                      }}
                    >
                      <div style={{
                        display: 'inline-block',
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: speaker.color || speaker.speakerColor || '#999',
                        marginRight: '8px'
                      }}></div>
                      {speaker.customLabel || speaker.speakerName || speaker.localLabel}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {onShiftTimestamps && (
              <button
                className="context-menu-item"
                onClick={() => {
                  onShiftTimestamps();
                  onClose();
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z"/>
                </svg>
                Shift Timestamps...
              </button>
            )}

            {onDistributeTimestamps && (
              <button
                className="context-menu-item"
                onClick={() => {
                  onDistributeTimestamps();
                  onClose();
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M4 4h16v2H4zm0 14h16v2H4zm0-7h16v2H4z"/>
                </svg>
                Distribute Evenly
              </button>
            )}
          </>
        )}

        {!hasSelectedText && !selectedWord && (
          <div className="context-menu-empty">
            <span>Right-click on text to see options</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContextMenu;